import {
  ActionsTooltip,
  Button,
  ButtonProps,
  List,
  Pagination,
} from '@flatfile/design-system'
import { useContext, useEffect } from 'react'
import { ErrorState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { getRuntimeParameters } from '../../components/RuntimeParameters'
import { SpaceContext } from '../../contexts/SpaceContext'
import { ButtonRow } from '../../elements/ButtonRow'
import { MainContent } from '../../elements/MainContent'
import { VerticalDivider } from '../../elements/VerticalDivider'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { ListContainer } from './components/ListElements'
import { EndProjectFooter, UpgradeBanner } from './components/UpgradeElements'
import { EditWorkbookAccessModal } from './modals/EditWorkbookAccessModal'
import { EndProjectModal } from './modals/EndProjectModal'
import { InviteGuestModal } from './modals/InviteGuestModal'
import { RemoveGuestModal } from './modals/RemoveGuestModal'
import { useManageProject } from './useManageProject'
import { PAGE_SIZE, listHeaders, useManageUsers } from './useManageUsers'

export const ManageUsersRoute = () => {
  const { isGuest } = getRuntimeParameters()
  const { sidebarCollapsed } = useContext(SpaceContext)

  useEffect(() => {
    sidebarCollapsed?.set(false)
  }, [])

  if (isGuest) {
    return (
      <MainContent data-testid='guest-error-view'>
        <ErrorState
          title='You do not have permission to view this page'
          message='Only admins can manage guests. If you think you are seeing this in error, please contact support.'
          refresh={false}
        />
      </MainContent>
    )
  }

  return <ManageUsersView />
}

export const ManageUsersView = () => {
  useDocumentTitle('Manage Guests')

  const {
    billingEnabled,
    isCollaborative,
    hasProjectsEnabled,
    onEndProject,
    openEndProjectModal,
    closeEndProjectModal,
    showEndProjectModal,
    onUpgradeToProject,
  } = useManageProject()

  return (
    <MainContent data-testid='admin-view'>
      <PageHeader title='Manage Guests' />

      {!isCollaborative && billingEnabled && (
        <UpgradeBanner
          hasProjectsEnabled={hasProjectsEnabled}
          onUpgradeProject={onUpgradeToProject}
        />
      )}
      {showEndProjectModal && (
        <EndProjectModal
          onClose={closeEndProjectModal}
          onConfirm={onEndProject}
        />
      )}
      <ManageGuestsList
        billingEnabled={billingEnabled}
        isCollaborative={isCollaborative}
      />

      {isCollaborative && billingEnabled && (
        <EndProjectFooter onClickEndProject={openEndProjectModal} />
      )}
    </MainContent>
  )
}

const InviteGuestsButton = ({
  showTooltip,
  showLimitTooltip,
  ...props
}: ButtonProps & { showTooltip?: boolean; showLimitTooltip?: boolean }) => {
  if (showTooltip) {
    /* c8 ignore start */
    const text = showLimitTooltip
      ? 'Please start a Project in order to invite more than one guest.'
      : 'Please configure shared link or magic link guest authentication on this space in order to invite guests.'
    const infoUrl = showLimitTooltip
      ? 'https://flatfile.com/docs/core-paths/collaborate'
      : 'https://flatfile.com/docs/developer-tools/environment#guest-authentication'
    return (
      <ActionsTooltip
        text={text}
        actions={[
          {
            text: 'Learn more',
            onClick: () => window.open(infoUrl, '_blank'),
          },
        ]}
      >
        <Button {...props} data-testid='invite-guests-button' />
      </ActionsTooltip>
    )
    /* c8 ignore stop */
  }

  return <Button {...props} data-testid='invite-guests-button' />
}

const ManageGuestsList = ({
  billingEnabled,
  isCollaborative,
}: {
  billingEnabled: boolean
  isCollaborative: boolean
}) => {
  const { space } = useContext(SpaceContext)

  const {
    showInviteModal,
    showRemoveModal,
    openInviteModal,
    openRemoveModal,
    closeInviteModal,
    closeRemoveModal,
    closeEditModal,
    onSendInvites,
    onResendSelectedInvites,
    onRemoveUsers,
    onUpdateUser,
    isRemovingUsers,
    isLoadingUsers,
    hasLoadError,
    users,
    workbooks,
    isLoadingWorkbooks,
    isWorkbooksError,
    selectedUsers,
    editUser,
    currentPage,
    onChangePage,
    listData,
    magicLinkEnabled,
    sharedLinkEnabled,
    showCopyMessage,
    copyInviteLink,
    guestInviteLink,
    hasInviteLimit,
    hasReachedInviteLimit,
  } = useManageUsers({ space, billingEnabled, isCollaborative })

  const disableButtons = hasLoadError
  const disableInviteButtons =
    disableButtons || hasReachedInviteLimit || !magicLinkEnabled
  const showCopyLink = sharedLinkEnabled && !magicLinkEnabled
  const showInviteTooltip =
    hasReachedInviteLimit || (!magicLinkEnabled && !sharedLinkEnabled)

  return (
    <>
      {showInviteModal && (
        <InviteGuestModal
          guestInviteLink={guestInviteLink}
          sendInvite={onSendInvites}
          onClose={closeInviteModal}
          copyInviteLink={copyInviteLink}
          showCopyMessage={showCopyMessage}
          hasInviteLimit={hasInviteLimit}
        />
      )}
      {showRemoveModal && (
        <RemoveGuestModal
          onClose={closeRemoveModal}
          onConfirm={onRemoveUsers}
          isRemovingUsers={isRemovingUsers}
          users={selectedUsers}
        />
      )}
      {!!editUser && (
        <EditWorkbookAccessModal
          user={editUser}
          workbooks={workbooks}
          onClose={closeEditModal}
          onSubmit={onUpdateUser}
          isLoadingWorkbooks={isLoadingWorkbooks}
          isWorkbooksError={isWorkbooksError}
        />
      )}

      <ButtonRow>
        <Button
          variant='tertiary'
          iconLeft='userRemove'
          onPress={openRemoveModal}
          label='Revoke access'
          isDisabled={selectedUsers.length < 1 || disableButtons}
          size='sm'
        />
        {!showCopyLink && (
          <Button
            variant='tertiary'
            iconLeft='mail'
            onPress={onResendSelectedInvites}
            label='Resend invite'
            isDisabled={
              selectedUsers.length < 1 ||
              (disableInviteButtons && !hasReachedInviteLimit)
            }
            size='sm'
          />
        )}
        <VerticalDivider />
        {showCopyLink ? (
          <Button
            variant='tertiary'
            iconLeft='link'
            onPress={copyInviteLink}
            isDisabled={disableButtons}
            label='Copy Space link'
            size='sm'
          />
        ) : (
          <InviteGuestsButton
            showTooltip={showInviteTooltip}
            showLimitTooltip={hasReachedInviteLimit}
            variant='tertiary'
            iconLeft='userAdd'
            onPress={openInviteModal}
            isDisabled={disableInviteButtons}
            label='Invite guest'
            size='sm'
          />
        )}
      </ButtonRow>

      <ListContainer>
        <List
          headers={listHeaders}
          data={listData}
          loading={isLoadingUsers}
          error={hasLoadError}
          components={{
            error: (
              <ErrorState title='Unable to load the users for this space' />
            ),
            empty: <>No guests to display</>,
          }}
        />
      </ListContainer>

      {users?.length > PAGE_SIZE && (
        <Pagination
          onPageChange={onChangePage}
          totalCount={users?.length}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
        />
      )}
    </>
  )
}
